import Popper from 'popper.js'

/**
 * v-popover directive — drop-in replacement for BV2's v-b-popover.
 *
 * Usage (same modifiers as v-b-popover):
 *   v-popover.hover.top="'Some text'"
 *   v-popover.click.bottom="'Some text'"
 *   v-popover.hover.right="{ customClass: 'hint-secondary-popover', content: 'Text' }"
 *
 * Supported placement modifiers: top, bottom, right, left, bottomleft, bottomright, topleft, topright
 * Trigger modifiers: hover (default), click
 */

const BV2_PLACEMENT_MAP = {
    bottomleft: 'bottom-start',
    bottomright: 'bottom-end',
    topleft: 'top-start',
    topright: 'top-end',
}

function getOptions(value) {
    if (!value) return { content: '', customClass: '' }
    if (typeof value === 'string') return { content: value, customClass: '' }
    return {
        content: value.content ?? '',
        customClass: value.customClass ?? '',
    }
}

function getPlacement(modifiers) {
    const keys = Object.keys(modifiers)
    for (const key of keys) {
        if (key in BV2_PLACEMENT_MAP) return BV2_PLACEMENT_MAP[key]
        if (['top', 'bottom', 'right', 'left'].includes(key)) return key
    }
    return 'top'
}

function createPopoverEl(content, customClass, placement) {
    const el = document.createElement('div')
    const defaultClass = customClass ? '' : 'popover--v'
    el.className = ['popover', `bs-popover-${placement}`, defaultClass, customClass].filter(Boolean).join(' ')
    el.setAttribute('role', 'tooltip')
    el.innerHTML = `<div class="arrow"></div><div class="popover-body">${content}</div>`
    // pointer-events: none — prevents the popover from intercepting cursor events
    // width: max-content   — fits the box to text instead of stretching to max-width
    el.style.cssText = 'width: max-content; pointer-events: none; z-index: 1060; text-align: left;'
    document.body.appendChild(el)
    return el
}

function createState(el, binding) {
    const { content, customClass } = getOptions(binding.value)
    const rawPlacement = getPlacement(binding.modifiers)
    const bsPlacement = rawPlacement.split('-')[0]
    const isClick = !!binding.modifiers.click

    let popoverEl = null
    let popperInstance = null

    function syncPlacementClass(placement) {
        const base = placement.split('-')[0]
        popoverEl.className = popoverEl.className.replace(/\bbs-popover-\S+/g, `bs-popover-${base}`)
    }

    function show() {
        if (!content || popoverEl) return
        popoverEl = createPopoverEl(content, customClass, bsPlacement)
        popperInstance = new Popper(el, popoverEl, {
            placement: rawPlacement,
            modifiers: {
                arrow: { element: '.arrow' },
                preventOverflow: { boundariesElement: 'viewport' },
            },
            onCreate: (data) => syncPlacementClass(data.placement),
            onUpdate: (data) => syncPlacementClass(data.placement),
        })
        if (!isClick) {
            window.addEventListener('scroll', hide, { capture: true, passive: true })
        }
    }

    function hide() {
        window.removeEventListener('scroll', hide, { capture: true })
        if (popperInstance) { popperInstance.destroy(); popperInstance = null }
        if (popoverEl) { popoverEl.remove(); popoverEl = null }
    }

    function toggle(event) {
        event.stopPropagation()
        popoverEl ? hide() : show()
    }

    function hideOnDocumentClick() {
        hide()
    }

    if (isClick) {
        el.addEventListener('click', toggle)
        document.addEventListener('click', hideOnDocumentClick)
    } else {
        el.addEventListener('mouseenter', show)
        el.addEventListener('mouseleave', hide)
        if (el.matches(':hover')) show()
    }

    return {
        cleanup() {
            if (isClick) {
                el.removeEventListener('click', toggle)
                document.removeEventListener('click', hideOnDocumentClick)
            } else {
                el.removeEventListener('mouseenter', show)
                el.removeEventListener('mouseleave', hide)
            }
            hide()
        },
    }
}

export default {
    // Use `inserted` (not `bind`) so that el.parentNode is guaranteed to be set.
    inserted(el, binding) {
        el._popover = createState(el, binding)
    },

    // componentUpdated fires after the full component tree has re-rendered.
    // Only recreate state when content actually changed.
    componentUpdated(el, binding) {
        const { content: oldContent } = getOptions(binding.oldValue)
        const { content: newContent } = getOptions(binding.value)
        if (oldContent !== newContent) {
            if (el._popover) el._popover.cleanup()
            el._popover = createState(el, binding)
        }
    },

    unbind(el) {
        if (el._popover) {
            el._popover.cleanup()
            delete el._popover
        }
    },
}
